import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { ArrowLeft, Search, ShoppingCart, Star, Package, User, LogIn, Settings, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { BACKEND_URL } from '../config';
import { useCurrency } from '../contexts/CurrencyContext';
import Footer from './Footer';
import MainNavigation from './MainNavigation';

const ProductsPage = ({ navigateHome, navigateToProduct, navigateToCart, navigateToProfile, cart, addToCart }) => {
  const { formatPrice } = useCurrency();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    manufacturer: '',
    product_type: '',
    min_price: '',
    max_price: ''
  });

  // Load products when component mounts or filters change
  useEffect(() => {
    loadProducts();
  }, [currentPage, filters]);

  // Load categories and manufacturers
  useEffect(() => {
    loadCategories();
  }, []);

  const loadProducts = async () => {
    setLoading(true);
    try {

      // Build query parameters
      const params = new URLSearchParams({
        page: currentPage.toString(),
        per_page: '12'
      });

      if (filters.search) params.append('search', filters.search);
      if (filters.category) params.append('category', filters.category);
      if (filters.manufacturer) params.append('manufacturer', filters.manufacturer);
      if (filters.product_type) params.append('product_type', filters.product_type);
      if (filters.min_price) params.append('min_price', filters.min_price);
      if (filters.max_price) params.append('max_price', filters.max_price);

      const response = await fetch(`${BACKEND_URL}/api/products?${params}`);
      
      if (response.ok) {
        const data = await response.json();
        setProducts(data.products);
        setTotalPages(data.total_pages);
      } else {
        console.error('Failed to load products');
        setProducts([]);
      }
    } catch (error) {
      console.error('Error loading products:', error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/products/categories`);
      
      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories);
        setManufacturers(data.manufacturers);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const handleFilterChange = (key, value) => {
    // Convert "all" to empty string for backend filtering
    const filterValue = value === 'all' ? '' : value;
    setFilters({ ...filters, [key]: filterValue });
    setCurrentPage(1); // Reset to first page when filtering
  };

  const getStockBadge = (quantity) => {
    if (quantity === 0) {
      return <Badge variant="destructive">Out of Stock</Badge>;
    } else if (quantity < 5) {
      return <Badge variant="outline" className="border-brand-orange-500 text-brand-orange-600">Low Stock ({quantity})</Badge>;
    } else {
      return <Badge variant="outline" className="border-green-500 text-green-600">In Stock ({quantity})</Badge>;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header with Main Navigation */}
      <MainNavigation 
        onNavigate={(page) => {
          if (page === 'home') navigateHome();
          else if (page === 'cart') navigateToCart();
          else if (page === 'profile') navigateToProfile ? navigateToProfile() : window.location.href = '/profile';
          else window.location.href = `/${page}`;
        }} 
        currentPage="products" 
        cartCount={cart?.length || 0}
      />

      {/* Hero Banner - Modern Minimalistic Design */}
      <div className="relative bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-14 md:py-18">
            {/* Title with subtle accent */}
            <div className="max-w-4xl">
              <div className="inline-block mb-4">
                <span className="text-sm font-semibold text-[#0007b5] tracking-wider uppercase">3D Printing</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-medium text-slate-900 mb-6 page-title-uncage leading-tight">
                Shop
              </h1>
              <p className="text-lg md:text-xl text-slate-600 max-w-2xl leading-relaxed">
                Professional 3D printers, filaments, and accessories
              </p>
            </div>
            
            {/* Decorative element - subtle accent line */}
            <div className="mt-8 w-24 h-1 bg-gradient-to-r from-[#0007b5] to-blue-400"></div>
          </div>
        </div>
        
        {/* Background decorative elements */}
        <div className="absolute top-0 right-0 w-1/3 h-full opacity-5 pointer-events-none">
          <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-[#0007b5] rounded-full blur-3xl"></div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search products..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="pl-10"
              />
            </div>
            
            <Select value={filters.category} onValueChange={(value) => handleFilterChange('category', value)}>
              <SelectTrigger>
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(category => (
                  <SelectItem key={category} value={category}>{category}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filters.manufacturer} onValueChange={(value) => handleFilterChange('manufacturer', value)}>
              <SelectTrigger>
                <SelectValue placeholder="All Brands" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Brands</SelectItem>
                {manufacturers.map(manufacturer => (
                  <SelectItem key={manufacturer} value={manufacturer}>{manufacturer}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filters.product_type} onValueChange={(value) => handleFilterChange('product_type', value)}>
              <SelectTrigger>
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="FDM Printer">3D Printers (FDM)</SelectItem>
                <SelectItem value="Resin Printer">3D Printers (Resin)</SelectItem>
                <SelectItem value="Filament">Filaments</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex space-x-2">
              <Input
                type="number"
                placeholder="Min Price"
                value={filters.min_price}
                onChange={(e) => handleFilterChange('min_price', e.target.value)}
                className="w-full"
              />
              <Input
                type="number"
                placeholder="Max Price"
                value={filters.max_price}
                onChange={(e) => handleFilterChange('max_price', e.target.value)}
                className="w-full"
              />
            </div>
            <div className="flex justify-end">
              <Button 
                onClick={() => {
                  setFilters({
                    search: '',
                    category: '',
                    manufacturer: '',
                    product_type: '',
                    min_price: '',
                    max_price: ''
                  });
                  setCurrentPage(1);
                }}
                variant="outline"
              >
                Clear Filters
              </Button>
            </div>
          </div>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
                  <div className="w-full h-48 bg-slate-200 rounded-lg mb-4"></div>
                  <div className="h-4 bg-slate-200 rounded mb-2"></div>
                  <div className="h-3 bg-slate-200 rounded mb-4"></div>
                  <div className="h-8 bg-slate-200 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        ) : products.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {products.map(product => (
              <Card key={product.id} className="group hover:shadow-lg transition-all duration-300 border-0 shadow-md overflow-hidden cursor-pointer">
                <div className="relative" onClick={() => navigateToProduct(product)}>
                  {product.image_url ? (
                    <img
                      src={product.image_url}
                      alt={product.name}
                      className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-48 bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                      <Package className="w-16 h-16 text-slate-400" />
                    </div>
                  )}
                  <div className="absolute top-3 right-3">
                    {getStockBadge(product.stock_quantity)}
                  </div>
                </div>
                
                <CardHeader className="pb-3" onClick={() => navigateToProduct(product)}>
                  <div className="flex justify-between items-start mb-2">
                    <Badge variant="outline" className="text-xs">{product.manufacturer}</Badge>
                    {product.rating && (
                      <div className="flex items-center space-x-1">
                        <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                        <span className="text-xs text-slate-600">{product.rating}</span>
                      </div>
                    )}
                  </div>
                  <CardTitle className="text-lg leading-tight">{product.name}</CardTitle>
                  <CardDescription className="text-sm line-clamp-2">{product.description}</CardDescription>
                </CardHeader>
                
                <CardContent className="pt-0">
                  {/* Key Specifications */}
                  {product.specifications && (
                    <div className="mb-4 text-xs text-slate-600 space-y-1">
                      {product.specifications.build_volume && (
                        <div>Build: {product.specifications.build_volume}</div>
                      )}
                      {product.specifications.material_compatibility && (
                        <div>Material: {product.specifications.material_compatibility}</div>
                      )}
                      {product.specifications.diameter && (
                        <div>Diameter: {product.specifications.diameter}</div>
                      )}
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-2xl font-bold text-slate-800">{formatPrice(product.price)}</div>
                      {product.review_count > 0 && (
                        <div className="text-xs text-slate-500">({product.review_count} reviews)</div>
                      )}
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="mt-4 space-y-2">
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigateToProduct(product);
                      }}
                      variant="outline"
                      className="w-full"
                      size="sm"
                    >
                      View Details
                    </Button>
                    
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        addToCart(product);
                        alert(`${product.name} added to cart!`);
                      }}
                      disabled={product.stock_quantity === 0}
                      className={`w-full ${
                        product.stock_quantity === 0 
                          ? 'bg-slate-300 cursor-not-allowed' 
                          : 'bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900'
                      }`}
                      size="sm"
                    >
                      <ShoppingCart className="w-4 h-4 mr-1" />
                      {product.stock_quantity === 0 ? 'Out of Stock' : 'Add to Cart'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Package className="w-16 h-16 text-slate-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-600 mb-2">No products found</h3>
            <p className="text-slate-500">Try adjusting your filters or search terms</p>
          </div>
        )}

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="flex justify-center items-center space-x-4 mt-8">
            <Button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              variant="outline"
            >
              Previous
            </Button>
            
            <span className="text-slate-600">
              Page {currentPage} of {totalPages}
            </span>
            
            <Button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              variant="outline"
            >
              Next
            </Button>
          </div>
        )}
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default ProductsPage;